import { useTheme, LoginRequestModel, useDialog } from '@ecdlink/core';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ActionModal,
  Alert,
  BannerWrapper,
  Button,
  DialogPosition,
  Divider,
  FormInput,
  PasswordInput,
  Typography,
} from '@ecdlink/ui';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import * as styles from '@/pages/auth/login/login.styles';
import {
  initialLoginValues,
  LoginModel,
  loginSchema,
} from '@schemas/auth/login/login';
import { useAppDispatch } from '@store';
import { authActions, authThunkActions } from '@store/auth';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import ROUTES from '@routes/routes';
import { LxpLoginReturnModel } from '@/store/auth/auth.selectors';
import {
  clearOfflineCredentials,
  clearUserScopedStoreIfUserChanged,
} from '@/utils/user/user-scoped-store.utils';

const CryptoJS = require('crypto-js');
enum LoginErrorEnum {
  None,
  DetailsIncorrect,
  StrugglingLogin,
  UserAlreadyLoggedIn,
}

export const Login: React.FC = () => {
  const { theme } = useTheme();
  const history = useHistory();
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();
  const dialog = useDialog();
  const [isLoading, setIsLoading] = useState(false);
  const [displayError, setDisplayError] = useState('');
  const [idFieldVisible, setIdFieldVisible] = useState(true);
  const [loginAttempts, setLoginAttempts] = useState(0);

  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const {
    register: loginRegister,
    setValue: loginSetValue,
    formState: loginFormState,
    getValues: loginFormGetValues,
  } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: initialLoginValues,
    mode: 'onChange',
  });

  const { isValid, errors } = loginFormState;

  const getDisplayErrorMessage = (loginError: LoginErrorEnum): string => {
    switch (loginError) {
      case LoginErrorEnum.None:
        return '';
      case LoginErrorEnum.DetailsIncorrect:
        return 'Password or ID incorrect. Please try again';
      case LoginErrorEnum.StrugglingLogin:
        return 'Struggling to log in? Send a message to our help line!';
      case LoginErrorEnum.UserAlreadyLoggedIn:
        return `Another user is already logged in on this device. Please try again ${
          isOnline ? '' : 'when you are online'
        }`;
    }
  };
  const STRUGGLING_LOGIN_ATTEMPTS = 5;

  const loginUser = async (loginRequest: LoginRequestModel) => {
    setIsLoading(true);
    setDisplayError(getDisplayErrorMessage(LoginErrorEnum.None));

    try {
      const userIdHash = localStorage.getItem('userIdHash') || '';
      const userNameHash = localStorage.getItem('userNameHash') || '';
      const storedPassword = localStorage.getItem('userHash') || '';
      const userTokenHash = localStorage.getItem('userTokenHash') || '';
      const expiration = Number(
        localStorage.getItem('userLocalxpiration') || '0'
      );

      // Decrypt stored credentials
      const decryptedId = userIdHash
        ? CryptoJS.AES.decrypt(userIdHash, 'user id').toString(
            CryptoJS.enc.Utf8
          )
        : '';
      const decryptedUserName = userNameHash
        ? CryptoJS.AES.decrypt(userNameHash, 'user name').toString(
            CryptoJS.enc.Utf8
          )
        : '';
      const decryptedPassword = storedPassword
        ? CryptoJS.AES.decrypt(storedPassword, 'user pass').toString(
            CryptoJS.enc.Utf8
          )
        : '';

      // Offline login
      if (!isOnline) {
        if (!decryptedUserName || !decryptedPassword) {
          setDisplayError(
            'No offline credentials available. Please login online first.'
          );
          setIsLoading(false);
          return;
        }

        if (Date.now() > expiration) {
          setDisplayError(
            'Offline login expired. Please connect to the internet.'
          );
          setIsLoading(false);
          return;
        }

        if (
          loginRequest.username !== decryptedUserName ||
          loginRequest.password !== decryptedPassword
        ) {
          setDisplayError(
            getDisplayErrorMessage(LoginErrorEnum.DetailsIncorrect)
          );
          setIsLoading(false);
          return;
        }

        if (
          loginRequest.username === decryptedUserName &&
          loginRequest.password === decryptedPassword
        ) {
          const plainUser: LxpLoginReturnModel = {
            id: decryptedId,
            auth_token: userTokenHash,
            expires_in: expiration.toString(),
            isTempUser: false,
            resetData: false,
          };

          // Successful offline login
          await appDispatch(authActions.setUserOffline(plainUser));
          setLoginAttempts(0);
          history.push(ROUTES.DASHBOARD, { isFromLogin: true });
        } else {
          // Failed offline login
          setLoginAttempts((prev) => {
            const newAttempts = prev + 1;
            setDisplayError(
              newAttempts >= STRUGGLING_LOGIN_ATTEMPTS
                ? getDisplayErrorMessage(LoginErrorEnum.StrugglingLogin)
                : getDisplayErrorMessage(LoginErrorEnum.UserAlreadyLoggedIn)
            );
            return newAttempts;
          });
        }

        setIsLoading(false);
        return;
      }

      // Online login
      const resultAction: any = await appDispatch(
        authThunkActions.login(loginRequest)
      );

      if (
        authThunkActions.login.fulfilled.match(resultAction) &&
        resultAction.payload
      ) {
        // Offline-first: keep the previous user's data bag only when the same
        // user re-logs in. A different user must not inherit that offline cache.
        const switchedUser = clearUserScopedStoreIfUserChanged(
          resultAction.payload.id
        );
        if (switchedUser) {
          clearOfflineCredentials();
        }

        // Save encrypted credentials for offline login
        const userIdHash = CryptoJS.AES.encrypt(
          resultAction.payload.id,
          'user id'
        ).toString();
        const userNameHash = CryptoJS.AES.encrypt(
          loginRequest.username,
          'user name'
        ).toString();
        const userHash = CryptoJS.AES.encrypt(
          loginRequest.password,
          'user pass'
        ).toString();

        localStorage.setItem('userIdHash', userIdHash);
        localStorage.setItem('userHash', userHash);
        localStorage.setItem('userNameHash', userNameHash);
        localStorage.setItem('userTokenHash', resultAction.payload.auth_token);
        localStorage.setItem(
          'userLocalxpiration',
          (Date.now() + 3600000000).toString()
        );

        setLoginAttempts(0);
        history.push(ROUTES.DASHBOARD, { isFromLogin: true });
      } else {
        // Failed online login
        setLoginAttempts((prev) => {
          const newAttempts = prev + 1;
          setDisplayError(
            newAttempts >= STRUGGLING_LOGIN_ATTEMPTS
              ? getDisplayErrorMessage(LoginErrorEnum.StrugglingLogin)
              : getDisplayErrorMessage(LoginErrorEnum.DetailsIncorrect)
          );
          return newAttempts;
        });

        if (resultAction.payload?.lockedOut) {
          handleUserLockedOut();
        }
      }
    } catch (err) {
      console.error(err);
      setLoginAttempts((prev) => {
        const newAttempts = prev + 1;
        setDisplayError(
          newAttempts >= STRUGGLING_LOGIN_ATTEMPTS
            ? getDisplayErrorMessage(LoginErrorEnum.StrugglingLogin)
            : getDisplayErrorMessage(LoginErrorEnum.DetailsIncorrect)
        );
        return newAttempts;
      });
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const submitForm = async () => {
    if (!isValid) return;

    const loginRequest: LoginRequestModel = {
      username: loginFormGetValues().preferId
        ? loginFormGetValues().idField
        : loginFormGetValues().passportField,
      password: loginFormGetValues().password,
    };

    await loginUser(loginRequest);
  };

  const handleUserLockedOut = () => {
    dialog({
      position: DialogPosition.Middle,
      blocking: true,
      color: 'bg-white',
      render: (onClose) => {
        return (
          <ActionModal
            className={'mx-4'}
            title={`Oops! Too many login attempts!`}
            detailText={
              'Please try again later. If you are still struggling, please send a message to our helpline.'
            }
            icon={'ExclamationCircleIcon'}
            // iconSize={48}
            iconColor={'alertMain'}
            iconBorderColor={'white'}
          />
        );
      },
    });
  };

  function forgotPasswordClicked() {
    history.push(ROUTES.PASSWORD_RESET);
  }

  const onClickGetHelp = () => {
    dialog({
      position: DialogPosition.Middle,
      blocking: true,
      color: 'bg-white',
      render: (onClose) => {
        return (
          <ActionModal
            className={'mx-4'}
            title={`Please call our helpline`}
            detailText={'087 148 5229'}
            icon={'ExclamationCircleIcon'}
            iconColor={'alertMain'}
            iconBorderColor={'white'}
            actionButtons={[
              {
                text: 'Close',
                textColour: 'primary',
                colour: 'primary',
                type: 'outlined',
                onClick: onClose,
                leadingIcon: 'XIcon',
              },
            ]}
          />
        );
      },
    });
  };

  function toggleIdAndpassport(visible: boolean) {
    const flag = !visible;
    loginSetValue(flag ? 'passportField' : 'idField', '');
    loginSetValue('preferId', flag);
    setIdFieldVisible(flag);
  }

  return (
    <BannerWrapper
      size="medium"
      color="primary"
      renderBorder={false}
      showBackground={true}
      backgroundImageColour={'primary'}
      backgroundUrl={theme?.images.graphicOverlayUrl}
    >
      <form className={styles.loginContainer}>
        {idFieldVisible && (
          <FormInput<LoginModel>
            label={'ID number'}
            visible={true}
            nameProp={'idField'}
            register={loginRegister}
            error={errors['idField']}
            placeholder={'E.g. 7601010338089'}
          />
        )}
        {!idFieldVisible && (
          <FormInput<LoginModel>
            label={'Passport number'}
            visible={true}
            nameProp={'passportField'}
            error={errors['passportField']}
            register={loginRegister}
          />
        )}
        {!idFieldVisible && (
          <Button
            className={'mt-3 mb-2'}
            type="outlined"
            color="primary"
            background={'transparent'}
            size="small"
            onClick={() => toggleIdAndpassport(idFieldVisible)}
          >
            <Typography
              type="buttonSmall"
              color="primary"
              text={'Enter ID number instead'}
            />
          </Button>
        )}
        {idFieldVisible && (
          <Button
            className={'mt-3 mb-2'}
            type="outlined"
            color="primary"
            size="small"
            background={'transparent'}
            onClick={() => toggleIdAndpassport(idFieldVisible)}
          >
            <Typography
              type="buttonSmall"
              color="primary"
              text={'Enter passport number instead'}
            ></Typography>
          </Button>
        )}
        <PasswordInput<LoginModel>
          label={'Password'}
          className={'mt-1 mb-2'}
          nameProp={'password'}
          sufficIconColor={'uiMidDark'}
          value={loginFormGetValues().password}
          register={loginRegister}
        />

        <Button
          className={'mt-1 mb-3'}
          type="outlined"
          color="primary"
          background={'transparent'}
          size="small"
          onClick={forgotPasswordClicked}
        >
          <Typography
            type="buttonSmall"
            color="primary"
            text={'Forgot my password'}
          />
        </Button>

        <Divider dividerType="dashed" />

        {!!displayError && (
          <Alert
            className={'mt-5 mb-3'}
            title={displayError}
            type={'error'}
            button={
              displayError ===
              getDisplayErrorMessage(LoginErrorEnum.StrugglingLogin) ? (
                <Button
                  text="Get help"
                  icon={'QuestionMarkCircleIcon'}
                  type={'filled'}
                  color={'secondaryAccent2'}
                  textColor={'secondary'}
                  onClick={() => onClickGetHelp()}
                />
              ) : undefined
            }
          />
        )}
        <Button
          id="gtm-login"
          className={'mt-3 w-full'}
          type="filled"
          isLoading={isLoading}
          color="primary"
          disabled={!isValid}
          onClick={submitForm}
        >
          <Typography type="help" color="white" text={'Log in'} />
        </Button>
      </form>
    </BannerWrapper>
  );
};
